import type { ReactNode } from "react";
import { FileText, Loader2 } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { cn } from "@/modern/utils";
import type {
  ConvertAction,
  ConvertLeadPreview,
  ConvertPreviewFile,
} from "../api/convertLead.types";
import {
  convertActionLabel,
  formatPreviewAddress,
  linksSummary,
} from "../utils/convertLeadLabels";

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i;

function OutcomeNote({ action }: { action: ConvertAction }) {
  return (
    <p
      className={cn(
        "text-xs leading-snug",
        action === "skip" ? "text-muted-foreground/70" : "text-muted-foreground",
      )}
    >
      {convertActionLabel(action)}
    </p>
  );
}

function PreviewSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: ConvertAction;
  children?: ReactNode;
}) {
  return (
    <section className="space-y-2 py-3.5 first:pt-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        {action ? <OutcomeNote action={action} /> : null}
      </div>
      {children ? (
        <div className="space-y-2 rounded-lg border border-border/70 bg-muted/20 px-3.5 py-3">
          {children}
        </div>
      ) : null}
    </section>
  );
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <span className="w-16 shrink-0 text-xs font-medium text-muted-foreground">{children}</span>
  );
}

function InlineField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 items-baseline gap-3", className)}>
      <FieldLabel>{label}</FieldLabel>
      <div className="min-w-0 flex-1 text-sm leading-snug text-foreground">{children}</div>
    </div>
  );
}

function BlockField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

function parseTaskContent(task: ConvertLeadPreview["task"]): {
  heading: string | null;
  summary: string | null;
  notes: string[];
} {
  const agenda = task.agenda_title?.trim() || "";
  const displayName = task.display_name?.trim() || "";
  const description = task.description?.trim() || "";

  const lines = description
    ? description.split(/\n+/).map((line) => line.trim()).filter(Boolean)
    : [];

  const notes: string[] = [];
  const proseLines: string[] = [];

  for (const line of lines) {
    const bullet = line.match(/^[-•*]\s+(.*)$/);
    if (bullet) {
      notes.push(bullet[1].trim());
    } else {
      proseLines.push(line);
    }
  }

  let summary = proseLines.join(" ").trim() || null;

  // Prefer a short heading; long AI sentences belong in Summary, not as a title.
  const candidateHeading = agenda || displayName;
  const headingLooksLikeParagraph =
    candidateHeading.length > 80 || /[.!?].+\S/.test(candidateHeading);
  const heading = candidateHeading && !headingLooksLikeParagraph ? candidateHeading : null;

  if (!summary && headingLooksLikeParagraph && candidateHeading) {
    summary = candidateHeading;
  }

  // Drop summary when it only repeats the heading.
  if (summary && heading && summary === heading) {
    summary = null;
  }

  return { heading, summary, notes };
}

function isImageFile(file: ConvertPreviewFile): boolean {
  return IMAGE_EXT.test(file.filename || "");
}

function filePreviewSrc(file: ConvertPreviewFile): string | null {
  const src = file.thumbnail_url?.trim() || file.url?.trim() || "";
  return src || null;
}

function AttachmentPreview({ files }: { files: ConvertPreviewFile[] }) {
  if (files.length === 0) {
    return <p className="text-muted-foreground">No attachments to copy.</p>;
  }

  const images = files.filter((file) => isImageFile(file) && filePreviewSrc(file));
  const otherFiles = files.filter((file) => !images.includes(file));

  return (
    <div className="space-y-3">
      {images.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {images.map((file) => {
            const src = filePreviewSrc(file)!;
            return (
              <a
                key={file.submission_file_id}
                href={file.url?.trim() || src}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative aspect-square overflow-hidden rounded-md border border-border/70 bg-muted/30"
                title={file.filename}
              >
                <img
                  src={src}
                  alt={file.filename}
                  className="size-full object-cover transition-opacity group-hover:opacity-90"
                />
              </a>
            );
          })}
        </div>
      ) : null}

      {otherFiles.length > 0 ? (
        <ul className="space-y-1.5">
          {otherFiles.map((file) => (
            <li
              key={file.submission_file_id}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <FileText className="size-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-all">{file.filename}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function ConvertLeadDialog({
  open,
  onOpenChange,
  preview,
  loading,
  confirming,
  leadName,
  canConfirm,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: ConvertLeadPreview | null;
  loading: boolean;
  confirming: boolean;
  leadName: string;
  canConfirm: boolean;
  onConfirm: () => void;
}) {
  const busy = loading || confirming;
  const filesAction: ConvertAction =
    !preview || preview.files.length === 0
      ? "skip"
      : preview.files.some((file) => file.action === "create")
        ? "create"
        : preview.files[0].action;

  const resolvedName = leadName.trim() || "this lead";
  const taskContent = preview ? parseTaskContent(preview.task) : null;
  const alsoHappens = preview ? linksSummary(preview.links ?? []) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
    >
      <DialogContent className="z-[--swivu-bryntum-z-index] flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="shrink-0 space-y-1.5 border-b border-border/60 px-6 py-4 pr-12">
          <DialogTitle>Convert lead to task</DialogTitle>
          <DialogDescription>
            Here is what Swivu will set up for {resolvedName}. Nothing is saved until you convert.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {loading || !preview || !taskContent ? (
            <div className="flex flex-col items-center justify-center gap-2 py-14 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" aria-hidden />
              <p className="text-sm">Preparing preview…</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              <PreviewSection title="Contact" action={preview.contact.action}>
                <div className="space-y-1.5">
                  <InlineField label="Name">
                    <span className="font-medium">{preview.contact.name || "—"}</span>
                  </InlineField>
                  {preview.contact.email ? (
                    <InlineField label="Email">
                      <span className="break-all text-muted-foreground">
                        {preview.contact.email}
                      </span>
                    </InlineField>
                  ) : null}
                  {preview.contact.phone ? (
                    <InlineField label="Phone">
                      <span className="text-muted-foreground">{preview.contact.phone}</span>
                    </InlineField>
                  ) : null}
                  {preview.contact.action === "reuse" ? (
                    <p className="text-xs text-muted-foreground/65">
                      Matched an existing contact on this board by email.
                    </p>
                  ) : null}
                </div>
              </PreviewSection>

              <PreviewSection title="Client" action={preview.client.action}>
                <InlineField label="Name">
                  <span className="font-medium">{preview.client.name || "—"}</span>
                </InlineField>
              </PreviewSection>

              <PreviewSection title="Task" action={preview.task.action}>
                <div className="space-y-2.5">
                  {taskContent.heading ? (
                    <InlineField label="Title">
                      <span className="font-medium">{taskContent.heading}</span>
                    </InlineField>
                  ) : null}

                  {taskContent.summary ? (
                    <BlockField label="Summary">
                      <p className="rounded-md border border-border/50 bg-background/40 px-3 py-2 text-sm leading-relaxed text-muted-foreground">
                        {taskContent.summary}
                      </p>
                    </BlockField>
                  ) : null}

                  {taskContent.notes.length > 0 ? (
                    <BlockField label="Notes">
                      <ul className="list-disc space-y-1 pl-4 text-sm leading-snug text-muted-foreground">
                        {taskContent.notes.map((note) => (
                          <li key={note}>{note}</li>
                        ))}
                      </ul>
                    </BlockField>
                  ) : null}

                  {!taskContent.heading &&
                  !taskContent.summary &&
                  taskContent.notes.length === 0 ? (
                    <p className="text-muted-foreground">No task details provided.</p>
                  ) : null}
                </div>
              </PreviewSection>

              <PreviewSection title="Location" action={preview.location.action}>
                {preview.location.action === "skip" ? (
                  <p className="text-muted-foreground">This lead has no location to attach.</p>
                ) : (
                  <InlineField label="Address">
                    <span>{formatPreviewAddress(preview.location)}</span>
                  </InlineField>
                )}
              </PreviewSection>

              <PreviewSection title="Files" action={filesAction}>
                <AttachmentPreview files={preview.files} />
              </PreviewSection>

              {alsoHappens ? (
                <PreviewSection title="What else happens">
                  <p className="text-sm leading-snug text-muted-foreground">{alsoHappens}</p>
                </PreviewSection>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-border/60 px-6 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={busy || !preview || !canConfirm}
            onClick={onConfirm}
            className="gap-1.5"
          >
            {confirming ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            Convert to task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
